import React from 'react';
import {useTranslation} from 'react-i18next';

import {TStep as TStepperStep} from '@edna/components';

type TBaseStep = Record<string, string>;
type TStep<T> = keyof T;

type TStepState<T> = Required<TStepperStep<TStep<T>>>;

type TFormSteps<T extends TBaseStep> = {
  stepsState: TStepState<T>[];
  changeStep: (id: TStepperStep['id']) => void;
  goToNextStep: TEmptyFunction;
  goToPrevStep: TEmptyFunction;
  isFirstStep: boolean;
  isLastStep: boolean;
};

type TPayload<T extends TBaseStep> = {
  steps: TStep<T>[];
  labelPrefix: string;
  activeStep: TStep<T>;
  setActiveStep: React.Dispatch<React.SetStateAction<TStep<T>>>;
  isFormValid?: boolean;
  hasLabel?: boolean;
  showCompleted?: boolean;
};

export const useFormSteps = <T extends TBaseStep>({
  steps,
  labelPrefix,
  isFormValid,
  setActiveStep,
  activeStep,
  hasLabel = true,
  showCompleted = true,
}: TPayload<T>): TFormSteps<T> => {
  const {t} = useTranslation();

  const stepsState: TStepState<T>[] = React.useMemo(() => {
    const activeStepIndex = steps.indexOf(activeStep);

    return steps.map((step, index) => ({
      id: step,
      disabled: index - activeStepIndex > 1 || (index > activeStepIndex && !isFormValid),
      completed: showCompleted && index < activeStepIndex,
      label: hasLabel ? t(`${labelPrefix}.${step.toString()}`) : '',
    }));
  }, [t, activeStep, isFormValid, labelPrefix, steps, hasLabel, showCompleted]);

  const handleStepChange = React.useCallback(
    (step: string | number) => {
      if (step === activeStep) {
        return;
      }

      setActiveStep(step as TStep<T>);
    },
    [activeStep],
  );

  const goToNextStep = React.useCallback(() => {
    setActiveStep((currentStep) => {
      const currentStepIndex = steps.indexOf(currentStep);

      return steps[Math.min(currentStepIndex + 1, steps.length - 1)];
    });
  }, [steps]);

  const goToPrevStep = React.useCallback(() => {
    setActiveStep((currentStep) => {
      const currentStepIndex = steps.indexOf(currentStep);

      return steps[Math.max(currentStepIndex - 1, 0)];
    });
  }, [steps]);

  return {
    changeStep: handleStepChange,
    stepsState,
    goToNextStep,
    goToPrevStep,
    isFirstStep: activeStep === steps[0],
    isLastStep: activeStep === steps[steps.length - 1],
  };
};
